/**
 * "Can we still see the child, and can this machine keep up?" — the CAM-05 / CAM-06 watcher.
 *
 * GDS §36 gives tracking loss four rungs: pause, show a return-to-position overlay, resume without
 * penalty, offer an override or the keyboard. This file decides *when* each rung is due; the stage
 * decides what a family sees. Keeping the decision here and the surface there is what lets the
 * whole sequence be tested without a browser, a camera or a mission.
 *
 * **It invents no signal of its own.** The loss is the one the shipped tracking gate already
 * reports through `PLAYER_LEFT_FRAME` (D-062(1)) — the same threshold that decides a held posture
 * must be retracted, so a child can never be simultaneously "gone" for retraction and "present"
 * for the pause. Low performance is `PoseTrackerHealth.lowPerformance`, the seam Slice B built and
 * nothing consumed. What is added here, and only here, is the *debounce* PS3-3 asked for and the
 * patience the fallback offer needs.
 *
 * **Everything runs on the frame clock, deliberately.** No `setTimeout` anywhere: a monitor
 * counting on a wall clock would keep counting through exactly the situation it exists to notice —
 * frames that have stopped arriving. Every decision below is made on the timestamp of a frame that
 * was genuinely observed, which also makes the whole machine deterministic under test and under an
 * end-to-end run.
 *
 * **The two cases frames alone cannot report (Slice G, D-065(4)).** Both are still decided here, so
 * GDS §36's ladder has one home, and both reach the mission as the same ordinary pause:
 *
 *   - *The camera stopped sending pictures.* Frames stopping entirely is invisible to a frame clock,
 *     so the camera session — which owns the only clock that keeps running — pushes a **duration**
 *     in and this file decides what it means. That is the one wall clock in the machinery, and it
 *     lives where the stream does.
 *   - *Nobody ever arrived.* A sitting that opens on an empty room has no `PLAYER_LEFT_FRAME` to
 *     report, because nobody was there to lose; the rounds would be asked of an empty room. So an
 *     unacquired body becomes a loss episode on the same debounce, with a different invitation.
 *     Acquisition is read from the camera adapter's own presence rather than from
 *     `PLAYER_ENTERED_FRAME`, because in a camera sitting the keyboard adapter is attached too
 *     (CAM-09) and announces the same hero on attach — believing it would mean no room is ever
 *     empty.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { PlayerId } from '../../shared/types/ids';
import type { GameplayTunables } from '../../shared/types/tunables';
import type { SemanticEventBus } from '../events/semantic-event-bus';
import { createObservationContinuity } from '../pose/observation-continuity';
import type { PoseFrameSource } from './camera-input-adapter';

/** What the monitor believes right now. Three independent facts, never a single "mode". */
export interface TrackingRecoveryState {
  /** The child is not being tracked, and the mission should be held for them (CAM-05). */
  readonly trackingLost: boolean;
  /** The machine is not keeping up and the session has degraded honestly (CAM-06). */
  readonly lowPerformance: boolean;
  /** Either condition has lasted long enough to offer a grown-up the keyboard (GDS §36 rung 4). */
  readonly fallbackOffered: boolean;
}

/**
 * Why the mission is being held, when it is (Slice G).
 *
 * Deliberately *beside* {@link TrackingRecoveryState} rather than inside it: the state is what the
 * stage composes its pause from and nothing about the ladder changes with the reason. All it
 * decides is which invitation a child reads — "come back" is the wrong thing to say to a child who
 * never left, and to one whose camera has been unplugged.
 */
export type TrackingLossReason = 'ABSENT' | 'NEVER_ACQUIRED' | 'NO_FRAMES';

/** What travels with each announcement. Additive, so an existing listener reads exactly as before. */
export interface TrackingRecoveryDetail {
  readonly reason: TrackingLossReason | null;
}

/** The half of `PoseTrackerHealth` this monitor reads. Structural, so either source satisfies it. */
export interface LowPerformanceSignal {
  readonly lowPerformance: boolean;
}

/** How long the camera has been producing nothing at all. See `CameraSession.frameStarvation`. */
export interface FrameStarvationSignal {
  readonly starved: boolean;
  readonly starvedForMs: number;
}

export interface FrameStarvationSource {
  subscribe(listener: (state: FrameStarvationSignal) => void): () => void;
}

export interface TrackingRecoveryMonitorOptions {
  /** Where the adapter's presence events arrive. */
  bus: SemanticEventBus;
  /** Whose presence. Events for anybody else are somebody else's business. */
  playerId: PlayerId;
  /** The frame stream — the monitor's clock and its heartbeat. */
  frames: PoseFrameSource;
  /** The performance signal. Absent is legitimate: the tracking half still works. */
  health?: (() => LowPerformanceSignal) | undefined;
  /**
   * Whether the camera adapter can see the body right now.
   *
   * Absent means nothing is reading a body at all, and the empty-room episode is then switched off
   * entirely — a monitor with no camera behind it must never invent a missing child.
   */
  presence?: (() => boolean) | undefined;
  /** The camera session's frame watchdog. Absent means nobody is watching for a dead stream. */
  starvation?: FrameStarvationSource | undefined;
  tunables?: GameplayTunables;
  /** Called only when something actually changed. */
  onChange: (state: TrackingRecoveryState, detail: TrackingRecoveryDetail) => void;
}

export interface TrackingRecoveryMonitor {
  readonly state: TrackingRecoveryState;
  /** Why the mission is held, or null when it is not. */
  readonly lossReason: TrackingLossReason | null;
  /** Stops listening. Nothing is announced afterwards. */
  dispose(): void;
}

const CALM: TrackingRecoveryState = {
  trackingLost: false,
  lowPerformance: false,
  fallbackOffered: false,
};

export function createTrackingRecoveryMonitor(
  options: TrackingRecoveryMonitorOptions,
): TrackingRecoveryMonitor {
  const { bus, playerId, frames, onChange } = options;
  const { pose } = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const health = options.health ?? null;
  const presence = options.presence ?? null;

  let state: TrackingRecoveryState = CALM;
  let reason: TrackingLossReason | null = null;
  let disposed = false;

  /** The frame clock: the timestamp of the most recent frame actually observed. */
  let lastFrameAt: number | null = null;
  /** The first frame this monitor ever saw — where the empty-room episode is measured from. */
  let firstFrameAt: number | null = null;
  /** The body has left the picture, having been in it. */
  let bodyLost = false;
  /** Nobody has arrived yet, and enough time has passed to say so. */
  let neverAcquired = false;
  /** The camera adapter has reported a body at some point in this sitting. */
  let everAcquired = false;
  /** When the body was lost — or the room declared empty — on the frame clock. */
  let lostSince: number | null = null;
  /** When the body was seen again, while still lost — the debounce is measured from here. */
  let pendingReturnAt: number | null = null;
  /** When the machine started struggling, on the frame clock. */
  let slowSince: number | null = null;
  /** The camera itself has stopped producing anything (wall-clock, from the session). */
  let starved = false;
  let starvedForMs = 0;
  /** Frames have started again but have not yet earned belief. */
  let awaitingFrames = false;
  let framesReturningSince: number | null = null;
  /**
   * Whether this monitor is inside a run of frames it actually saw (S6-10).
   *
   * The shared predicate on the sitting's own bound — the same one every lane adapter, the resolver
   * and the calibration flows read, so "the run of frames ended" is one fact here too.
   */
  const continuity = createObservationContinuity(options.tunables);

  /** The composed picture, from whichever facts currently hold. */
  const compose = (
    at: number | null,
    lowPerformance: boolean = state.lowPerformance,
  ): TrackingRecoveryState => {
    const framesMissing = starved || awaitingFrames;
    const trackingLost = framesMissing || bodyLost || neverAcquired;

    // Rung 4: any condition, having lasted. The offer stands while any still holds, so a machine
    // that catches up while the child is still out of shot does not withdraw it.
    const lastingLoss =
      trackingLost && lostSince !== null && at !== null && at - lostSince >= pose.fallbackOfferMs;
    const lastingSlowness =
      lowPerformance && slowSince !== null && at !== null && at - slowSince >= pose.fallbackOfferMs;
    const lastingSilence = starved && starvedForMs >= pose.fallbackOfferMs;
    const fallbackOffered =
      (trackingLost || lowPerformance) &&
      (lastingLoss || lastingSlowness || lastingSilence || state.fallbackOffered);

    return { trackingLost, lowPerformance, fallbackOffered };
  };

  const reasonNow = (): TrackingLossReason | null => {
    if (starved || awaitingFrames) return 'NO_FRAMES';
    if (neverAcquired) return 'NEVER_ACQUIRED';
    if (bodyLost) return 'ABSENT';
    return null;
  };

  const announce = (next: TrackingRecoveryState): void => {
    const nextReason = reasonNow();
    if (
      next.trackingLost === state.trackingLost &&
      next.lowPerformance === state.lowPerformance &&
      next.fallbackOffered === state.fallbackOffered &&
      nextReason === reason
    ) {
      return;
    }
    state = next;
    reason = nextReason;
    onChange(state, { reason });
  };

  const unsubscribes = [
    bus.subscribe('PLAYER_LEFT_FRAME', (event) => {
      if (disposed || event.playerId !== playerId) return;
      // A child cannot leave a picture they were never in: a departure is itself proof that the
      // camera acquired them, which is what takes the empty-room episode off the table.
      everAcquired = true;
      // A pending return dies with the child who was making it: the debounce restarts from the
      // next sighting, not from the one that turned out to be a flicker.
      pendingReturnAt = null;
      if (bodyLost) return;
      bodyLost = true;
      lostSince ??= lastFrameAt;
      announce(compose(lastFrameAt));
    }),

    bus.subscribe('PLAYER_ENTERED_FRAME', (event) => {
      if (disposed || event.playerId !== playerId) return;
      // Nothing to recover from, and nothing to debounce: a keyboard adapter announcing itself on
      // attach reaches this line too, and it must not start a clock about a camera.
      if (!state.trackingLost) return;
      pendingReturnAt ??= lastFrameAt;
    }),

    frames.subscribe((frame) => {
      if (disposed) return;

      /**
       * **A clock that measures how long things have been *right* may not count a hole** (S6-10).
       *
       * The clocks in this file divide cleanly, and the division is the whole of this rule:
       *
       *  - `lostSince`, `slowSince` and the empty-room `firstFrameAt` measure **elapsed wrongness**.
       *    They span a hole deliberately and must — a hole does not make a problem younger, and a
       *    child who has been missing for four seconds has been missing for four seconds whether or
       *    not anybody was watching. Resetting those on a hole would mean a family is never told
       *    anything, because every clock would be perpetually young.
       *  - `framesReturningSince` and `pendingReturnAt` measure **elapsed rightness** — how long the
       *    frames have been arriving properly, and how long the child has been back in view. Those
       *    are claims about watched time, in exactly the sense D-090 gives the phrase, and an
       *    interval nobody observed is not time anything was going well for.
       *
       * Two frames half a second apart used to satisfy the 250 ms `trackingReacquireMs` between
       * them, so the pause lifted on a stream that had just proved it was still stuttering — "one
       * frame is a camera waking up rather than working", the rule these clocks exist to enforce,
       * defeated by counting the gap between two wake-ups. So a hole **restarts** them from the
       * first frame that was actually observed after it; it does not clear them, because a cleared
       * `pendingReturnAt` waits for a `PLAYER_ENTERED_FRAME` that a child already in view will never
       * send again.
       */
      if (continuity.note(frame.detectedAt)) {
        if (framesReturningSince !== null) framesReturningSince = frame.detectedAt;
        if (pendingReturnAt !== null) pendingReturnAt = frame.detectedAt;
      }

      lastFrameAt = frame.detectedAt;
      firstFrameAt ??= frame.detectedAt;

      // Frames are flowing again, but one frame is a camera waking up rather than working.
      if (awaitingFrames) {
        framesReturningSince ??= frame.detectedAt;
        if (frame.detectedAt - framesReturningSince >= pose.trackingReacquireMs) {
          awaitingFrames = false;
          framesReturningSince = null;
        }
      }

      /**
       * The empty-room episode (D-065(4)).
       *
       * Read from the adapter's own presence rather than from the bus, and only where an adapter
       * exists: a monitor with nothing reading a body must never invent a missing child.
       */
      if (presence !== null && !everAcquired) {
        if (presence()) {
          everAcquired = true;
          if (neverAcquired) pendingReturnAt ??= frame.detectedAt;
        } else if (
          !neverAcquired &&
          firstFrameAt !== null &&
          frame.detectedAt - firstFrameAt >= pose.trackingLossPauseMs
        ) {
          neverAcquired = true;
          lostSince ??= frame.detectedAt;
        }
      }

      // Rung 3: the child has been there for long enough to believe it — whether they came back or
      // arrived for the first time.
      if (
        (bodyLost || neverAcquired) &&
        pendingReturnAt !== null &&
        frame.detectedAt - pendingReturnAt >= pose.trackingReacquireMs
      ) {
        bodyLost = false;
        neverAcquired = false;
        pendingReturnAt = null;
        lostSince = null;
      }

      const lowPerformance = health?.().lowPerformance === true;
      if (!lowPerformance) slowSince = null;
      else slowSince ??= frame.detectedAt;

      announce(compose(frame.detectedAt, lowPerformance));
    }),
  ];

  /**
   * The camera itself, watched by the one clock that survives it stopping (Slice G).
   *
   * The session reports a duration and this decides what it means, so a dead stream reaches a
   * family through exactly the ladder a wandering child does.
   */
  if (options.starvation !== undefined) {
    unsubscribes.push(
      options.starvation.subscribe((next) => {
        if (disposed) return;
        const wasStarved = starved;
        starved = next.starved;
        starvedForMs = next.starved ? next.starvedForMs : 0;

        if (starved) {
          lostSince ??= lastFrameAt;
          // Nothing is arriving, so nothing can be part-way back.
          pendingReturnAt = null;
        } else if (wasStarved) {
          awaitingFrames = true;
          framesReturningSince = null;
        }

        announce(compose(lastFrameAt));
      }),
    );
  }

  return {
    get state(): TrackingRecoveryState {
      return state;
    },

    get lossReason(): TrackingLossReason | null {
      return reason;
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const unsubscribe of unsubscribes) unsubscribe();
    },
  };
}
